import { Navigate, useParams } from "react-router-dom"
import { useAdminAccess } from "@/hooks/useAdminAccess"
import { useAuth } from "@/hooks/use-auth"
import { Loader2 } from "lucide-react"

export interface ProtectedAdminRouteProps {
  children: React.ReactNode
  requiredRole?: "platform_admin" | "course_admin"
  courseId?: string
}

export function ProtectedAdminRoute({
  children,
  requiredRole = "platform_admin",
  courseId: courseIdProp,
}: ProtectedAdminRouteProps) {
  const params = useParams()
  const courseId = courseIdProp || params.courseId
  const { user } = useAuth()
  const { adminAccess, isLoading, isPlatformAdmin, isCourseAdmin, managedCourses, error } = useAdminAccess()

  // Additional frontend validation for platform admin email domain
  const isValidPlatformAdminEmail = user?.email?.endsWith('@nodoublesgolfco.com') ?? false

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Checking admin access...</p>
        </div>
      </div>
    )
  }

  // Show error if there's one
  if (adminAccess === undefined && !isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Error Loading Admin Access</h1>
          <p className="text-muted-foreground mb-4">
            Unable to verify admin permissions. Please check the browser console for details.
          </p>
          <p className="text-sm text-muted-foreground font-mono bg-muted p-2 rounded">
            {error?.message || "Unknown error"}
          </p>
        </div>
      </div>
    )
  }

  // Check platform admin access with email domain validation
  if (requiredRole === "platform_admin") {
    if (!isPlatformAdmin || !isValidPlatformAdminEmail) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              Platform administrator access is restricted to No Doubles Golf Co. team members.
            </p>
            {!isValidPlatformAdminEmail && user?.email && (
              <p className="text-sm text-muted-foreground mb-4 font-mono bg-muted p-2 rounded">
                Email: {user.email}
              </p>
            )}
            <Navigate to="/" replace />
          </div>
        </div>
      )
    }
  }

  // Check course admin access
  if (requiredRole === "course_admin") {
    if (!isCourseAdmin) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              You need course administrator privileges to access this page.
            </p>
            <Navigate to="/" replace />
          </div>
        </div>
      )
    }

    // If courseId is specified, verify user manages that course
    if (courseId) {
      // Handle both string and number courseId (managedCourses.id is string, params might be number)
      const managesCourse = managedCourses.some((c) => 
        c.id === courseId || c.id === String(courseId) || String(c.id) === courseId
      )
      if (!managesCourse && !isPlatformAdmin) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
              <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
              <p className="text-muted-foreground mb-6">
                You don't have administrator access to this course.
              </p>
              <Navigate to="/clubhouse" replace />
            </div>
          </div>
        )
      }
    }
  }

  return <>{children}</>
}

